export const YOUCAT_QUIZ_SETTINGS_COLLECTION = "youcatQuizSettings";
export const YOUCAT_QUIZ_SETTINGS_DOC_ID = "public";
export const YOUCAT_QUIZ_USERS_COLLECTION = "youcatQuizUsers";
export const YOUCAT_QUIZ_SUBMISSIONS_COLLECTION = "youcatQuizSubmissions";

export type YoucatQuizQuestionType = "mcq" | "short_answer" | "long_answer";

export type YoucatQuizQuestion = {
  id: string;
  question: string;
  type: YoucatQuizQuestionType;
  required: boolean;
  points: number;
  options: string[];
  correctOption: string;
  acceptedAnswers: string[];
  keywords: string[];
};

export type YoucatQuizSettings = {
  quizId: string;
  isQuizAvailable: boolean;
  title: string;
  description: string;
  instructions: string;
  startAt: string;
  endAt: string;
  totalDurationSeconds: number;
  recommendedDurationSeconds: number;
  minimumDurationSeconds: number;
  questions: YoucatQuizQuestion[];
  updatedAt?: string;
};

export type YoucatQuizUserProfile = {
  uid: string;
  fullName: string;
  deanery: string;
  parish: string;
  email: string;
  contact: string;
  createdAt?: string;
  updatedAt?: string;
};

export type YoucatQuizEvaluation = {
  matchedKeywords: string[];
  isCorrect: boolean;
  autoAwardedPoints: number;
  maxPoints: number;
};

export type YoucatQuizSubmission = {
  docId: string;
  quizId: string;
  quizTitle: string;
  userUid: string;
  userProfile: YoucatQuizUserProfile;
  status: "in_progress" | "submitted" | "expired";
  approvalStatus: "pending" | "approved" | "rejected";
  startedAt: string;
  submittedAt?: string;
  expiresAt: string;
  currentQuestionIndex: number;
  answers: Record<string, string>;
  questionSnapshots: YoucatQuizQuestion[];
  evaluationByQuestion: Record<string, YoucatQuizEvaluation>;
  autoScore: number;
  maxScore: number;
  finalScore?: number;
  finalScoresByQuestion?: Record<string, number>;
  reviewedAt?: string;
  reviewedBy?: string;
  reviewRemarks?: string;
};

export const EMPTY_QUIZ_QUESTION: YoucatQuizQuestion = {
  id: "",
  question: "",
  type: "mcq",
  required: false,
  points: 1,
  options: ["Option 1", "Option 2"],
  correctOption: "",
  acceptedAnswers: [],
  keywords: [],
};

export const DEFAULT_QUIZ_SETTINGS: YoucatQuizSettings = {
  quizId: "active-youcat-quiz",
  isQuizAvailable: false,
  title: "YouCat Quiz",
  description:
    "Enable this when the quiz login/signup flow should be visible on the public YouCat page.",
  instructions:
    "Read each question carefully. Use Save & Next after each question. When the timer reaches zero, your answered questions will be submitted automatically for admin review.",
  startAt: "",
  endAt: "",
  totalDurationSeconds: 0,
  recommendedDurationSeconds: 0,
  minimumDurationSeconds: 0,
  questions: [],
};

export function getQuestionBaseTimeSeconds(type: YoucatQuizQuestionType) {
  if (type === "mcq") {
    return 30;
  }

  if (type === "short_answer") {
    return 45;
  }

  return 60;
}

export function computeBaseDurationSeconds(questions: YoucatQuizQuestion[]) {
  return questions.reduce(
    (total, question) => total + getQuestionBaseTimeSeconds(question.type),
    0,
  );
}

export function computeRecommendedDurationSeconds(
  questions: YoucatQuizQuestion[],
) {
  return Math.ceil(computeBaseDurationSeconds(questions) * 1.5);
}

export function computeMinimumDurationSeconds(questions: YoucatQuizQuestion[]) {
  return Math.ceil(computeBaseDurationSeconds(questions) * 0.5);
}

export function normalizeAnswer(value: string) {
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}

export function slugifyQuestionId(value: string) {
  const slug = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return slug || `question-${Date.now()}`;
}

export function formatDurationLabel(totalSeconds: number) {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export function formatQuizDateTime(value?: string) {
  if (!value) {
    return "Not set";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Invalid date";
  }

  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Asia/Kolkata",
  }).format(date);
}

export function buildQuizSubmissionDocId(quizId: string, uid: string) {
  return `${quizId}__${uid}`;
}

function splitNormalizedTokens(value: string) {
  return normalizeAnswer(value)
    .split(/[^a-z0-9]+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function getEditDistance(left: string, right: string) {
  const rows = left.length + 1;
  const cols = right.length + 1;
  const matrix = Array.from({ length: rows }, () => Array<number>(cols).fill(0));

  for (let row = 0; row < rows; row += 1) {
    matrix[row][0] = row;
  }

  for (let col = 0; col < cols; col += 1) {
    matrix[0][col] = col;
  }

  for (let row = 1; row < rows; row += 1) {
    for (let col = 1; col < cols; col += 1) {
      const cost = left[row - 1] === right[col - 1] ? 0 : 1;
      matrix[row][col] = Math.min(
        matrix[row - 1][col] + 1,
        matrix[row][col - 1] + 1,
        matrix[row - 1][col - 1] + cost,
      );
    }
  }

  return matrix[left.length][right.length];
}

function areSimilarTerms(left: string, right: string) {
  const normalizedLeft = normalizeAnswer(left);
  const normalizedRight = normalizeAnswer(right);

  if (!normalizedLeft || !normalizedRight) {
    return false;
  }

  if (
    normalizedLeft === normalizedRight ||
    normalizedLeft.includes(normalizedRight) ||
    normalizedRight.includes(normalizedLeft)
  ) {
    return true;
  }

  const maxDistance =
    Math.max(normalizedLeft.length, normalizedRight.length) >= 8 ? 2 : 1;

  return getEditDistance(normalizedLeft, normalizedRight) <= maxDistance;
}

function keywordMatchesAnswer(answer: string, keyword: string) {
  const normalizedAnswer = normalizeAnswer(answer);
  const normalizedKeyword = normalizeAnswer(keyword);

  if (!normalizedAnswer || !normalizedKeyword) {
    return false;
  }

  if (areSimilarTerms(normalizedAnswer, normalizedKeyword)) {
    return true;
  }

  const answerTokens = splitNormalizedTokens(normalizedAnswer);
  const keywordTokens = splitNormalizedTokens(normalizedKeyword);

  return keywordTokens.every((keywordToken) =>
    answerTokens.some((answerToken) => areSimilarTerms(answerToken, keywordToken)),
  );
}

export function evaluateQuizQuestion(
  question: YoucatQuizQuestion,
  answer: string,
): YoucatQuizEvaluation {
  const normalizedAnswer = normalizeAnswer(answer);
  const normalizedAcceptedAnswers = question.acceptedAnswers.map(normalizeAnswer);
  const normalizedKeywords = question.keywords.map(normalizeAnswer).filter(Boolean);

  if (!normalizedAnswer) {
    return {
      matchedKeywords: [],
      isCorrect: false,
      autoAwardedPoints: 0,
      maxPoints: question.points,
    };
  }

  if (question.type === "mcq") {
    const isCorrect =
      normalizedAnswer === normalizeAnswer(question.correctOption || "");
    return {
      matchedKeywords: [],
      isCorrect,
      autoAwardedPoints: isCorrect ? question.points : 0,
      maxPoints: question.points,
    };
  }

  if (question.type === "short_answer") {
    const matchedAcceptedAnswer = normalizedAcceptedAnswers.find((item) =>
      keywordMatchesAnswer(normalizedAnswer, item),
    );
    const matchedKeyword = normalizedKeywords.find((item) =>
      keywordMatchesAnswer(normalizedAnswer, item),
    );
    const isAccepted = Boolean(matchedAcceptedAnswer || matchedKeyword);

    return {
      matchedKeywords: [matchedAcceptedAnswer, matchedKeyword].filter(
        Boolean,
      ) as string[],
      isCorrect: isAccepted,
      autoAwardedPoints: isAccepted ? question.points : 0,
      maxPoints: question.points,
    };
  }

  const matchedKeywords = normalizedKeywords.filter((keyword) =>
    keywordMatchesAnswer(normalizedAnswer, keyword),
  );

  const autoAwardedPoints = normalizedKeywords.length
    ? Number(
        (
          (Math.min(matchedKeywords.length, normalizedKeywords.length) /
            normalizedKeywords.length) *
          question.points
        ).toFixed(2),
      )
    : 0;

  return {
    matchedKeywords,
    isCorrect: matchedKeywords.length > 0,
    autoAwardedPoints,
    maxPoints: question.points,
  };
}

export function evaluateQuizSubmission(
  questions: YoucatQuizQuestion[],
  answers: Record<string, string>,
) {
  const evaluationByQuestion = questions.reduce<
    Record<string, YoucatQuizEvaluation>
  >((acc, question) => {
    acc[question.id] = evaluateQuizQuestion(question, answers[question.id] || "");
    return acc;
  }, {});

  const autoScore = Object.values(evaluationByQuestion).reduce(
    (total, item) => total + item.autoAwardedPoints,
    0,
  );

  const maxScore = questions.reduce((total, question) => total + question.points, 0);

  return {
    evaluationByQuestion,
    autoScore: Number(autoScore.toFixed(2)),
    maxScore,
  };
}
